/**
 * Registry-driven label helpers (WSRPDM-0011).
 *
 * Job types / notification types / job statuses / severities render from
 * the enum value + the i18n catalog, never from a hardcoded switch
 * statement — an unmapped value (e.g. a new job type the catalog hasn't
 * caught up to yet) falls through to the raw enum instead of breaking.
 * WSRPDM-0012–0017 screens consume these; do not re-declare a parallel
 * label map in a screen.
 */

export type TFn = (key: string, fallback?: string) => string;

export const jobTypeLabel = (jobType: string, t: TFn): string => t(`jobType.${jobType}`, jobType);

/**
 * `notifType.<TYPE>` is a nested `{ label, title, body }` object per type
 * (WSRPDM-0015, contract §4.1) — `.title`/`.body` are the per-type feed
 * templates the Notifications screen renders from `type`+`params`; `.label`
 * is this short catalog label (a JSON resource key can't be both a string
 * and a parent of nested keys, so the flat string this used to read pre-0015
 * moved under `.label`). Raw-enum fallback unchanged for an unmapped type.
 */
export const notificationTypeLabel = (notifType: string, t: TFn): string =>
  t(`notifType.${notifType}.label`, notifType);

export const statusLabel = (status: string, t: TFn): string => t(`jobStatus.${status}`, status);

export const severityLabel = (severity: string, t: TFn): string =>
  t(`severity.${severity}`, severity);

/** True when the value has no catalog entry — the raw enum is what's on screen. */
export const isUnmappedJobType = (jobType: string, t: TFn): boolean =>
  t(`jobType.${jobType}`, '') === '';

export const isUnmappedNotificationType = (notifType: string, t: TFn): boolean =>
  t(`notifType.${notifType}.label`, '') === '';

export const isUnmappedStatus = (status: string, t: TFn): boolean =>
  t(`jobStatus.${status}`, '') === '';

export const isUnmappedSeverity = (severity: string, t: TFn): boolean =>
  t(`severity.${severity}`, '') === '';
